import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import ContactForm from '../components/home/ContactForm';

// Styled Components
const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
  color: white;
  padding: 100px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto 2.5rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--primary);
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: all var(--transition-normal);
  
  &:hover {
    background-color: var(--bg-light);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-lg);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 2rem auto;
`;

const FeatureContent = styled.div`
  padding: 0 2rem 2rem;
  text-align: center;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: var(--text-light);
  line-height: 1.6;
`;

const DetailedFeatureSection = styled.section`
  padding: 100px 0;
  background-color: ${props => props.dark ? 'var(--bg-light)' : 'var(--bg-white)'};
`;

const DetailedFeatureContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FeatureTextContent = styled.div`
  @media (max-width: 992px) {
    order: ${props => props.imageFirst ? 2 : 1};
  }
`;

const FeatureImageContent = styled.div`
  @media (max-width: 992px) {
    order: ${props => props.imageFirst ? 1 : 2};
    margin-bottom: 2rem;
  }
`;

const DetailedFeatureTitle = styled.h2`
  font-size: 2.25rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const DetailedFeatureDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  margin-bottom: 1.5rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FeaturePointsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeaturePoint = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const FeaturePointIcon = styled.div`
  color: var(--primary);
  margin-right: 1rem;
  flex-shrink: 0;
`;

const FeaturePointText = styled.p`
  flex: 1;
  font-size: 1rem;
  
  @media (max-width: 992px) {
    max-width: 400px;
  }
`;

const TechSection = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TechCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  text-align: center;
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-md);
  }
`;

const TechIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 1.5rem;
`;

const TechTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const TechDescription = styled.p`
  color: var(--text-light);
  font-size: 0.9rem;
`;

const CTASection = styled.section`
  padding: 80px 0;
  background-color: var(--bg-white);
  text-align: center;
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PrimaryCTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: all var(--transition-normal);
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    color: white;
  }
`;

// SVG Icons
const CustomizableIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M66.6667 13.3333L50 30M10 70L26.6667 53.3333M40 40L13.3333 66.6667M40 40L53.3333 26.6667M40 40L66.6667 13.3333M40 40L26.6667 53.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="40" cy="40" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <circle cx="66.6667" cy="13.3333" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <circle cx="13.3333" cy="66.6667" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <circle cx="26.6667" cy="53.3333" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <circle cx="53.3333" cy="26.6667" r="6.66667" stroke="#4338CA" strokeWidth="3" />
    </svg>
);

const ShoppingCartIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26.6667" cy="70" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <circle cx="60" cy="70" r="6.66667" stroke="#4338CA" strokeWidth="3" />
        <path d="M3.33325 6.66667H16.6666L25.6666 50.8333C26.0039 52.4737 26.9299 53.9457 28.2702 54.9873C29.6106 56.0289 31.2768 56.5738 32.9999 56.5H57.9999C59.723 56.5738 61.3892 56.0289 62.7296 54.9873C64.07 53.9457 64.996 52.4737 65.3333 50.8333L69.9999 23.3333H19.9999" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M40 20V36.6667" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M31.6667 28.3333H48.3334" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PaymentIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6.66663" y="16.6667" width="66.6667" height="46.6667" rx="4" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.66663 33.3333H73.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 50H33.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="53.3333" cy="50" r="6.66667" stroke="#4338CA" strokeWidth="3" />
    </svg>
);

const AnalyticsIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M73.3333 66.6667H6.66663V13.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M23.3333 50L36.6666 36.6667L50.0001 50L73.3334 26.6667" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="73.3333" cy="26.6667" r="6.66667" stroke="#4338CA" strokeWidth="3" />
    </svg>
);

const InventoryIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60 33.3333V73.3333M20 73.3333V33.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M73.3334 73.3333H6.66675" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M73.3334 20H6.66675" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M70 20L40 6.66666L10 20" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="33.3333" y="33.3333" width="13.3333" height="13.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="33.3333" y="53.3333" width="13.3333" height="13.3333" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const NotificationIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60 26.6667C60 21.3623 57.8929 16.2753 54.1421 12.5245C50.3914 8.77379 45.3043 6.66666 40 6.66666C34.6957 6.66666 29.6086 8.77379 25.8579 12.5245C22.1071 16.2753 20 21.3623 20 26.6667C20 50 10 56.6667 10 56.6667H70C70 56.6667 60 50 60 26.6667Z" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M45.7667 70C45.1267 71.1766 44.1908 72.1498 43.0532 72.8105C41.9157 73.4712 40.6207 73.7897 39.3 73.7299C37.9793 73.7897 36.6843 73.4712 35.5468 72.8105C34.4092 72.1498 33.4733 71.1766 32.8333 70" stroke="#4338CA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="60" cy="20" r="10" fill="#F59E0B" />
    </svg>
);

// Feature Detail Images
const MobileAppImage = () => (
    <svg width="100%" height="100%" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="120" y="40" width="160" height="320" rx="20" fill="#F3F4F6" stroke="#E5E7EB" strokeWidth="2" />
        <rect x="130" y="60" width="140" height="280" rx="10" fill="#F9FAFB" />

        {/* App Header */}
        <rect x="130" y="60" width="140" height="50" rx="10" fill="#4338CA" />
        <circle cx="150" cy="85" r="10" fill="white" fillOpacity="0.3" />
        <rect x="170" y="80" width="80" height="10" rx="5" fill="white" />

        {/* Product Card */}
        <rect x="140" y="120" width="120" height="100" rx="5" fill="white" />
        <rect x="150" y="130" width="100" height="60" rx="3" fill="#F3F4F6" />
        <rect x="150" y="200" width="70" height="8" rx="2" fill="#9CA3AF" />
        <rect x="150" y="190" width="40" height="5" rx="2" fill="#6B7280" />

        {/* Navigation */}
        <rect x="140" y="310" width="120" height="20" rx="10" fill="#F3F4F6" />
        <circle cx="165" cy="320" r="8" fill="#4338CA" />
        <circle cx="200" cy="320" r="8" fill="#D1D5DB" />
        <circle cx="235" cy="320" r="8" fill="#D1D5DB" />

        {/* Phone Details */}
        <circle cx="200" cy="50" r="5" fill="#D1D5DB" />

        {/* Decorative Elements */}
        <circle cx="340" cy="120" r="30" fill="#EEF2FF" />
        <circle cx="60" cy="200" r="40" fill="#EEF2FF" />
        <path d="M40 260C50 250 60 270 70 260C80 250 90 270 100 260" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
        <path d="M300 170C310 160 320 180 330 170C340 160 350 180 360 170" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const AnalyticsImage = () => (
    <svg width="100%" height="100%" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="white" />

        {/* Dashboard background */}
        <rect x="40" y="40" width="320" height="320" rx="10" fill="#F3F4F6" />

        {/* Header */}
        <rect x="40" y="40" width="320" height="60" rx="10" fill="#4338CA" />
        <circle cx="70" cy="70" r="15" fill="white" fillOpacity="0.3" />
        <rect x="100" y="65" width="100" height="10" rx="5" fill="white" />
        <circle cx="340" cy="70" r="15" fill="white" fillOpacity="0.3" />

        {/* Chart Card */}
        <rect x="60" y="120" width="180" height="120" rx="8" fill="white" />
        <rect x="70" y="130" width="100" height="10" rx="5" fill="#4B5563" />
        <path d="M70 200L100 180L130 190L160 170L190 180L220 160" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
        <circle cx="100" cy="180" r="3" fill="#4338CA" />
        <circle cx="130" cy="190" r="3" fill="#4338CA" />
        <circle cx="160" cy="170" r="3" fill="#4338CA" />
        <circle cx="190" cy="180" r="3" fill="#4338CA" />
        <circle cx="220" cy="160" r="3" fill="#4338CA" />

        {/* Stats Cards */}
        <rect x="260" y="120" width="80" height="50" rx="8" fill="white" />
        <rect x="270" y="130" width="60" height="8" rx="4" fill="#9CA3AF" />
        <rect x="270" y="150" width="40" height="10" rx="5" fill="#4338CA" />

        <rect x="260" y="180" width="80" height="50" rx="8" fill="white" />
        <rect x="270" y="190" width="60" height="8" rx="4" fill="#9CA3AF" />
        <rect x="270" y="210" width="40" height="10" rx="5" fill="#10B981" />

        {/* Data Table */}
        <rect x="60" y="260" width="280" height="80" rx="8" fill="white" />
        <rect x="70" y="270" width="260" height="20" rx="4" fill="#F3F4F6" />
        <rect x="80" y="275" width="40" height="10" rx="2" fill="#4B5563" />
        <rect x="140" y="275" width="40" height="10" rx="2" fill="#4B5563" />
        <rect x="200" y="275" width="40" height="10" rx="2" fill="#4B5563" />
        <rect x="260" y="275" width="40" height="10" rx="2" fill="#4B5563" />

        <rect x="80" y="300" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="140" y="300" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="200" y="300" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="260" y="300" width="40" height="10" rx="2" fill="#4338CA" />

        <rect x="80" y="320" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="140" y="320" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="200" y="320" width="40" height="10" rx="2" fill="#6B7280" />
        <rect x="260" y="320" width="40" height="10" rx="2" fill="#10B981" />
    </svg>
);

const PaymentSystemImage = () => (
    <svg width="100%" height="100%" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="white" />

        {/* Credit Card */}
        <rect x="50" y="100" width="300" height="200" rx="20" fill="#4338CA" />
        <rect x="70" y="130" width="60" height="40" rx="5" fill="#A5B4FC" />
        <rect x="70" y="220" width="120" height="20" rx="5" fill="#A5B4FC" />
        <rect x="70" y="250" width="60" height="20" rx="5" fill="#A5B4FC" />
        <rect x="140" y="250" width="60" height="20" rx="5" fill="#A5B4FC" />
        <circle cx="310" cy="130" r="20" fill="#EEF2FF" />
        <circle cx="290" cy="150" r="20" fill="#C7D2FE" />

        {/* Payment Icons */}
        <circle cx="100" cy="340" r="30" fill="#F3F4F6" />
        <rect x="85" y="335" width="30" height="10" rx="5" fill="#4B5563" />

        <circle cx="180" cy="340" r="30" fill="#F3F4F6" />
        <rect x="165" y="335" width="30" height="10" rx="5" fill="#4B5563" />
        <rect x="165" y="350" width="20" height="5" rx="2.5" fill="#4B5563" />

        <circle cx="260" cy="340" r="30" fill="#F3F4F6" />
        <path d="M245 340C245 335 250 330 260 330C270 330 275 335 275 340C275 345 270 350 260 350C250 350 245 345 245 340Z" fill="#4B5563" />

        <circle cx="340" cy="340" r="30" fill="#F3F4F6" />
        <rect x="325" y="335" width="30" height="10" rx="5" fill="#4B5563" />

        {/* Top Decorations */}
        <circle cx="100" cy="50" r="30" fill="#EEF2FF" />
        <circle cx="300" cy="50" r="30" fill="#EEF2FF" />
        <path d="M150 50C160 40 170 60 180 50C190 40 200 60 210 50C220 40 230 60 240 50" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Tech Icons for Integration Section
const ReactIcon = () => (
    <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="40" stroke="#4338CA" strokeWidth="2" fill="none" />
        <circle cx="50" cy="50" r="8" fill="#4338CA" />
        <ellipse cx="50" cy="50" rx="20" ry="40" stroke="#4338CA" strokeWidth="2" fill="none" transform="rotate(0 50 50)" />
        <ellipse cx="50" cy="50" rx="20" ry="40" stroke="#4338CA" strokeWidth="2" fill="none" transform="rotate(60 50 50)" />
        <ellipse cx="50" cy="50" rx="20" ry="40" stroke="#4338CA" strokeWidth="2" fill="none" transform="rotate(120 50 50)" />
    </svg>
);

const CloudIcon = () => (
    <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M75 65C82.7 65 89 58.7 89 51C89 43.3 82.7 37 75 37C74.7 37 74.3 37 74 37.1C72.3 28.4 64.6 22 55.5 22C45.2 22 37 30.2 37 40.5C37 41.3 37.1 42.2 37.2 43C36.5 42.9 35.8 42.8 35 42.8C26.2 42.8 19 50 19 58.8C19 67.6 26.2 74.8 35 74.8H75" stroke="#4338CA" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ApiIcon = () => (
    <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="15" y="30" width="70" height="40" rx="5" stroke="#4338CA" strokeWidth="3" fill="none" />
        <path d="M15 45H85" stroke="#4338CA" strokeWidth="2" />
        <path d="M30 60L40 55L30 50" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M50 60H60" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
        <circle cx="75" cy="55" r="5" stroke="#4338CA" strokeWidth="2" fill="none" />
    </svg>
);

const DbIcon = () => (
    <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="50" cy="30" rx="30" ry="15" stroke="#4338CA" strokeWidth="2" fill="none" />
        <path d="M20 30V70C20 78.3 33.4 85 50 85C66.6 85 80 78.3 80 70V30" stroke="#4338CA" strokeWidth="2" fill="none" />
        <path d="M20 50C20 58.3 33.4 65 50 65C66.6 65 80 58.3 80 50" stroke="#4338CA" strokeWidth="2" fill="none" />
    </svg>
);

// FeaturesPage 元件
const FeaturesPage = () => {
    // 核心功能數據
    const coreFeatures = [
        {
            id: 1,
            icon: <CustomizableIcon />,
            title: "客製化品牌體驗",
            description: "完全依照您的品牌調整介面設計、色彩和風格，創造獨特的購物體驗。"
        },
        {
            id: 2,
            icon: <ShoppingCartIcon />,
            title: "直覺的購物流程",
            description: "優化的用戶介面設計，讓購物流程更加順暢，提高轉換率和客戶滿意度。"
        },
        {
            id: 3,
            icon: <PaymentIcon />,
            title: "多元支付整合",
            description: "支援信用卡、行動支付等多種付款方式，滿足不同客戶的需求。"
        },
        {
            id: 4,
            icon: <AnalyticsIcon />,
            title: "強大的分析工具",
            description: "深入了解客戶行為和銷售數據，幫助您做出更明智的商業決策。"
        },
        {
            id: 5,
            icon: <InventoryIcon />,
            title: "庫存與訂單管理",
            description: "簡化庫存追蹤和訂單處理，即時更新庫存狀態，提升營運效率。"
        },
        {
            id: 6,
            icon: <NotificationIcon />,
            title: "推播通知",
            description: "透過即時通知保持與客戶的聯繫，推廣新品和特別優惠，增加顧客回購率。"
        }
    ];

    // 技術整合數據
    const techIntegrations = [
        {
            id: 1,
            icon: <ReactIcon />,
            title: "React Native",
            description: "使用最先進的框架，確保應用程式運作順暢"
        },
        {
            id: 2,
            icon: <CloudIcon />,
            title: "雲端架構",
            description: "確保高可用性與可擴展性"
        },
        {
            id: 3,
            icon: <ApiIcon />,
            title: "RESTful API",
            description: "易於與其他系統整合"
        },
        {
            id: 4,
            icon: <DbIcon />,
            title: "安全資料儲存",
            description: "符合最高安全標準的資料保護"
        }
    ];

    return (
        <PageWrapper>
            <HeroSection>
                <Container>
                    <HeroTitle>強大而靈活的功能</HeroTitle>
                    <HeroSubtitle>
                        ShopWave 提供完整的行動商務解決方案，幫助您的業務在數位時代蓬勃發展。從客製化設計到進階分析，我們的功能涵蓋您所需的一切。
                    </HeroSubtitle>
                    <CTAButton to="/contact">免費諮詢</CTAButton>
                </Container>
            </HeroSection>

            <FeaturesSection>
                <Container>
                    <SectionHeader>
                        <SectionTitle>核心功能</SectionTitle>
                        <SectionDescription>
                            ShopWave 提供完整的功能套件，幫助您打造成功的電子商務業務
                        </SectionDescription>
                    </SectionHeader>

                    <FeatureGrid>
                        {coreFeatures.map(feature => (
                            <FeatureCard key={feature.id}>
                                <FeatureIcon>{feature.icon}</FeatureIcon>
                                <FeatureContent>
                                    <FeatureTitle>{feature.title}</FeatureTitle>
                                    <FeatureDescription>{feature.description}</FeatureDescription>
                                </FeatureContent>
                            </FeatureCard>
                        ))}
                    </FeatureGrid>
                </Container>
            </FeaturesSection>

            <DetailedFeatureSection dark>
                <Container>
                    <DetailedFeatureContent>
                        <FeatureTextContent>
                            <DetailedFeatureTitle>用戶體驗優先的行動應用</DetailedFeatureTitle>
                            <DetailedFeatureDescription>
                                我們的行動購物應用以用戶體驗為核心，確保您的客戶可以輕鬆瀏覽產品、完成購買並獲得卓越的購物體驗。
                            </DetailedFeatureDescription>

                            <FeaturePointsList>
                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        直覺的產品瀏覽和分類系統，幫助客戶快速找到所需商品
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        簡化的結帳流程，減少購物車放棄率，提高轉換率
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        個人化推薦功能，根據客戶的瀏覽和購買歷史提供相關商品
                                    </FeaturePointText>
                                </FeaturePoint>
                            </FeaturePointsList>
                        </FeatureTextContent>

                        <FeatureImageContent>
                            <MobileAppImage />
                        </FeatureImageContent>
                    </DetailedFeatureContent>
                </Container>
            </DetailedFeatureSection>

            <DetailedFeatureSection>
                <Container>
                    <DetailedFeatureContent>
                        <FeatureImageContent imageFirst>
                            <AnalyticsImage />
                        </FeatureImageContent>

                        <FeatureTextContent imageFirst>
                            <DetailedFeatureTitle>深入的數據分析與報表</DetailedFeatureTitle>
                            <DetailedFeatureDescription>
                                獲取關鍵業務洞見，了解您的客戶行為、銷售趨勢和市場機會，做出數據驅動的決策，提升業務績效。
                            </DetailedFeatureDescription>

                            <FeaturePointsList>
                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        實時儀表板顯示銷售趨勢、客戶行為和庫存狀態
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        定制報表和數據導出功能，支援不同業務部門的需求
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        客戶細分和行為分析，幫助您優化行銷策略和產品佈局
                                    </FeaturePointText>
                                </FeaturePoint>
                            </FeaturePointsList>
                        </FeatureTextContent>
                    </DetailedFeatureContent>
                </Container>
            </DetailedFeatureSection>

            <DetailedFeatureSection dark>
                <Container>
                    <DetailedFeatureContent>
                        <FeatureTextContent>
                            <DetailedFeatureTitle>安全可靠的支付系統</DetailedFeatureTitle>
                            <DetailedFeatureDescription>
                                我們的支付系統整合了多種支付方式，確保交易安全，提供無縫的購物體驗，幫助您拓展業務並提高客戶滿意度。
                            </DetailedFeatureDescription>

                            <FeaturePointsList>
                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        支援信用卡、金融卡、行動支付等多種支付方式
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        符合國際支付卡產業資料安全標準 (PCI DSS)，確保交易安全
                                    </FeaturePointText>
                                </FeaturePoint>

                                <FeaturePoint>
                                    <FeaturePointIcon>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </FeaturePointIcon>
                                    <FeaturePointText>
                                        完整的交易紀錄和對帳功能，簡化財務管理
                                    </FeaturePointText>
                                </FeaturePoint>
                            </FeaturePointsList>
                        </FeatureTextContent>

                        <FeatureImageContent>
                            <PaymentSystemImage />
                        </FeatureImageContent>
                    </DetailedFeatureContent>
                </Container>
            </DetailedFeatureSection>

            <TechSection>
                <Container>
                    <SectionHeader>
                        <SectionTitle>先進技術平台</SectionTitle>
                        <SectionDescription>
                            ShopWave 建立在最新的技術架構上，確保應用的穩定性、安全性和可擴展性
                        </SectionDescription>
                    </SectionHeader>

                    <TechGrid>
                        {techIntegrations.map(tech => (
                            <TechCard key={tech.id}>
                                <TechIcon>{tech.icon}</TechIcon>
                                <TechTitle>{tech.title}</TechTitle>
                                <TechDescription>{tech.description}</TechDescription>
                            </TechCard>
                        ))}
                    </TechGrid>
                </Container>
            </TechSection>

            <CTASection>
                <CTAContainer>
                    <CTATitle>準備好開始轉型您的電商業務了嗎？</CTATitle>
                    <CTADescription>
                        立即聯繫我們，了解 ShopWave 如何幫助您的業務成長。我們的專家團隊隨時準備為您提供個人化的諮詢服務。
                    </CTADescription>
                    <PrimaryCTAButton to="/contact">免費諮詢</PrimaryCTAButton>
                </CTAContainer>
            </CTASection>

            <ContactForm />
        </PageWrapper>
    );
};

export default FeaturesPage;